// React, Next
import { ReactElement, useContext } from 'react'
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Icons
import WidgetsIcon from '@mui/icons-material/Widgets'
import TroubleshootIcon from '@mui/icons-material/Troubleshoot'
import TuneIcon from '@mui/icons-material/Tune'
import CloudDoneIcon from '@mui/icons-material/CloudDone'
import LockIcon from '@mui/icons-material/Lock'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import { GlobalContext } from 'context/GlobalContextProvider'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBodySmall } from '@/assets/theme/fonts'

export default function WhyUsSection() {
  const { t } = useTranslation('solutionsIotAirQualityMonitoring')
  const { screenDimensions } = useContext(GlobalContext)

  const w = screenDimensions.width
  const iconSx = { fontSize: '1.8rem', color: themeColors.bg.start }

  const reasons: { icon: ReactElement; title: string; description: string }[] =
    [
      {
        icon: <WidgetsIcon sx={iconSx} />,
        title: t('whyUs.items.01.title'),
        description: t('whyUs.items.01.description')
      },
      {
        icon: <TroubleshootIcon sx={iconSx} />,
        title: t('whyUs.items.02.title'),
        description: t('whyUs.items.02.description')
      },
      {
        icon: <TuneIcon sx={iconSx} />,
        title: t('whyUs.items.03.title'),
        description: t('whyUs.items.03.description')
      },
      {
        icon: <CloudDoneIcon sx={iconSx} />,
        title: t('whyUs.items.04.title'),
        description: t('whyUs.items.04.description')
      },
      {
        icon: <LockIcon sx={iconSx} />,
        title: t('whyUs.items.05.title'),
        description: t('whyUs.items.05.description')
      }
    ]

  const getGrid = () => {
    if (w > breakpoints.desktopToMobile) return 'repeat(3, 1fr)'
    if (w > breakpoints.mobileMid) return 'repeat(2, 1fr)'
    return 'repeat(1, 1fr)'
  }

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <SectionHeader eyebrow={t('whyUs.eyebrow')} title={t('whyUs.title')} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: getGrid(),
          gap: '1.5rem',
          alignItems: 'stretch'
        }}
      >
        {reasons.map(reason => (
          <Box
            key={reason.title}
            sx={{
              height: '100%',
              background: themeColors.section.light,
              border: `1px solid ${themeColors.gray.lighter}`,
              borderRadius: '10px',
              padding: '1.5rem',
              boxSizing: 'border-box'
            }}
          >
            <Box sx={{ lineHeight: 0, marginBottom: '1rem' }}>
              {reason.icon}
            </Box>

            <Typography
              variant="h3"
              component="h3"
              sx={{
                fontSize: '1rem',
                fontWeight: 700,
                lineHeight: 1.3,
                color: themeColors.kroemtech.dunkelblau,
                marginBottom: '0.6rem'
              }}
            >
              {reason.title}
            </Typography>

            <Typography
              component="p"
              sx={{
                fontSize: getFontBodySmall(),
                lineHeight: 1.65,
                color: themeColors.gray.defaultText
              }}
            >
              {reason.description}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.kroemtech.background}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
